"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.App = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const lodash_1 = __importDefault(require("lodash"));
const assembly_1 = require("../assembly");
const values_1 = __importDefault(require("../values"));
const react_bootstrap_1 = require("react-bootstrap");
const Container_1 = __importDefault(require("react-bootstrap/Container"));
const smap_1 = require("../smap");
const version_1 = require("../version");
const ItemDisplay_1 = require("./ItemDisplay");
const useSimulation_1 = require("../hooks/useSimulation");
const useCalculateRates_1 = require("../hooks/useCalculateRates");
const numberFormatter_1 = require("../numberFormatter");
const ReleaseNotes_1 = require("./ReleaseNotes");
const bigmath_1 = require("../bigmath");
const ClickAmountButtons_1 = require("./ClickAmountButtons");
require("./App.scss");
const useMinigames_1 = require("../hooks/useMinigames");
const Sprite_1 = require("./Sprite");
const MAX_BIG = BigInt(Number.MAX_VALUE);
const MULTI_CLICK_OPTIONS = {
    "1": (0, bigmath_1.NumToBig)(1),
    "10": (0, bigmath_1.NumToBig)(10),
    "100": (0, bigmath_1.NumToBig)(100),
    "MAX": MAX_BIG,
};
const SATISFY_CLICK_OPTIONS = {
    "SATISFY": 0n,
};
function App({ ticksPerSecond }) {
    const { assemblers, amountThatWeHave, visible, doAction, acknowledged, canMakeItemByHand, disabledRecipes, state, fps, } = (0, useSimulation_1.useProduction)(ticksPerSecond);
    let [currentTab, setCurrentTab] = (0, react_1.useState)(null);
    const [currentClickAmount, setCurrentClickAmount] = (0, react_1.useState)(MULTI_CLICK_OPTIONS["1"]);
    const [isPlayingMinigame, setIsPlayingMinigame] = (0, react_1.useState)(false);
    const [miniGameConfig, setMiniGameConfig] = (0, react_1.useState)(null);
    const [miniGamePrize, setMiniGamePrize] = (0, react_1.useState)(null);
    const { getMiniGame, resetMinigame, pickMinigameByItem } = (0, useMinigames_1.useMinigames)();
    const MiniGameClass = isPlayingMinigame ? getMiniGame(miniGameConfig?.minigame) : null;
    function stopMinigame() {
        resetMinigame();
        setIsPlayingMinigame(false);
    }
    const sections = {};
    const sectionData = values_1.default.sections.find((x) => x.Name == currentTab);
    const rates = (0, useCalculateRates_1.useCalculateRates)(state, sectionData?.SubSections.flatMap((x) => x.Items) ?? []);
    function calculateMaxMake(itemName, n) {
        return (0, bigmath_1.bigMin)((0, bigmath_1.bigLt)(currentClickAmount, 1) ? (0, bigmath_1.NumToBig)(1) : currentClickAmount, (0, assembly_1.howManyRecipesCanBeMade)(itemName, state.amountThatWeHave), state.calculateStorage(itemName) - n, values_1.default.maxCraftAtATime(itemName, state));
    }
    function calculateBuildingsToSatisfy(building, recipe) {
        const consumption = (0, bigmath_1.bigSum)((0, smap_1.values)(rates.effectiveConsumptionRates[recipe] ?? {})) +
            (0, bigmath_1.bigSum)((0, smap_1.values)(rates.powerConsumptionRates[recipe] ?? {}).map(x => x[2]));
        const production = (0, bigmath_1.bigSum)((0, smap_1.values)(rates.effectiveProductionRates[recipe] ?? {}));
        const speed = (0, bigmath_1.NumToBig)(values_1.default.assemblerSpeeds[building] * values_1.default.calculateBoost(building, state) / values_1.default.timePerRecipe[recipe]);
        if (speed === 0n)
            return (0, bigmath_1.NumToBig)(1);
        return (0, bigmath_1.bigCeil)((0, bigmath_1.bigDiv)(consumption - production, speed));
    }
    function calculateMaxAdd(itemName, target) {
        const amt = currentClickAmount === 0n && target
            ? calculateBuildingsToSatisfy(itemName, target)
            : currentClickAmount;
        return (0, bigmath_1.bigMin)((0, bigmath_1.bigLt)(amt, 1) ? (0, bigmath_1.NumToBig)(1) : amt, state.amountThatWeHave[itemName] ?? 0n);
    }
    const haveAssemblers = values_1.default.allAssemblers.filter((key) => (amountThatWeHave[key] ?? 0) > 0);
    if (currentTab === null) {
        setCurrentTab(lodash_1.default.values(values_1.default.sections).filter((x) => x.SubSections.some((ss) => ss.Items.some((item) => visible[item])))[0].Name);
        return null;
    }
    for (const subSection of sectionData?.SubSections ?? []) {
        sections[currentTab] ??= [];
        const elements = sections[currentTab];
        const thisSectionItems = [];
        for (const itemName of subSection.Items) {
            if (!visible[itemName])
                continue;
            const amt = amountThatWeHave[itemName] ?? 0n;
            const recipe = values_1.default.recipes[itemName];
            if (recipe === undefined)
                continue;
            const buildingsToMakeThis = values_1.default.requiredBuildings(itemName);
            const makeByHand = canMakeItemByHand(itemName);
            const assemblerCount = assemblers[itemName];
            const assemblersMakingThis = lodash_1.default.pickBy(assemblerCount, (x) => x !== 0n);
            const assemblerButtons = [];
            const boxButtons = [];
            values_1.default.itemsCanBeStoreIn[itemName].forEach((container) => {
                if (!state.visible[container])
                    return;
                if (state.hideAddButtons[container])
                    return;
                const num = calculateMaxAdd(container);
                const disabled = (0, bigmath_1.bigLt)(amountThatWeHave[container] ?? 0n, 1);
                boxButtons.push((0, jsx_runtime_1.jsxs)(react_bootstrap_1.Button, { className: "add-container", onClick: () => {
                        doAction({
                            action: 'add-box',
                            amount: num,
                            box: container,
                            recipe: itemName,
                        });
                    }, variant: "info", disabled: disabled, children: ["Add ", num > 0 ? (0, numberFormatter_1.formatNumber)(num) : '', " ", values_1.default.displayNames(container)] }, container));
            });
            buildingsToMakeThis.forEach((assemblerName) => {
                if (assemblerName === 'by-hand')
                    return;
                if (!state.visible[assemblerName])
                    return;
                if (state.hideAddButtons[assemblerName])
                    return;
                const a = assemblerName;
                const haveAny = haveAssemblers.includes(assemblerName);
                const num = calculateMaxAdd(a, itemName);
                assemblerButtons.push((0, jsx_runtime_1.jsxs)(react_bootstrap_1.Button, { className: "add-assembler", onClick: () => {
                        doAction({
                            action: 'add-building',
                            amount: num,
                            building: a,
                            recipe: itemName,
                        });
                    }, variant: "secondary", disabled: !haveAny, children: ["Add ", num > 0 ? (0, numberFormatter_1.formatNumber)(num) : '', " ", values_1.default.displayNames(assemblerName)] }, assemblerName));
            });
            if (pickMinigameByItem(itemName) && (0, bigmath_1.bigGt)(amountThatWeHave['research-minigames'] ?? 0n, 0)) {
                assemblerButtons.push((0, jsx_runtime_1.jsx)(react_bootstrap_1.Button, { onClick: () => {
                        setIsPlayingMinigame(true);
                        setMiniGameConfig(pickMinigameByItem(itemName));
                        setMiniGamePrize(itemName);
                    }, children: "Play a Mini Game" }, 'playminigame'));
            }
            thisSectionItems.push((0, jsx_runtime_1.jsx)(ItemDisplay_1.ItemDisplay, { amt: amt ?? 0n, state: state, assemblersMakingThis: assemblersMakingThis, boxButtons: boxButtons, itemName: itemName, assemblerButtons: assemblerButtons, currentClickAmount: currentClickAmount, makeByHand: makeByHand === null
                    ? null
                    : makeByHand === false
                        ? false
                        : () => {
                            doAction({
                                action: 'craft-byhand',
                                amount: calculateMaxMake(itemName, amt),
                                recipe: itemName,
                            });
                        }, disableRecipe: () => !disabledRecipes[itemName] ? doAction({
                    action: 'disable-recipe',
                    recipe: itemName
                }) : doAction({
                    action: 'enable-recipe',
                    recipe: itemName,
                }), onMouseover: acknowledged[itemName] !== true
                    ? () => {
                        doAction({
                            action: 'ack',
                            recipe: itemName,
                        });
                    }
                    : undefined, ...rates }, itemName));
        }
        if (thisSectionItems.length > 0) {
            elements.push((0, jsx_runtime_1.jsxs)("div", { className: "subsection-header", children: [subSection.Icon && ((0, jsx_runtime_1.jsx)(Sprite_1.Sprite, { name: subSection.Icon })), subSection.Name] }, "ss-" + subSection.Name));
            elements.push(...thisSectionItems);
        }
    }
    let multiClickOptions = {};
    if ((0, bigmath_1.bigEq)(amountThatWeHave["research-mass-click"], 1)) {
        multiClickOptions = {
            ...multiClickOptions,
            ...MULTI_CLICK_OPTIONS,
        };
    }
    if ((0, bigmath_1.bigEq)(amountThatWeHave["research-satisfy-button"], 1)) {
        multiClickOptions = {
            ...multiClickOptions,
            ...SATISFY_CLICK_OPTIONS,
        };
    }
    return ((0, jsx_runtime_1.jsxs)(Container_1.default, { fluid: true, className: "game-container noselect", children: [(0, jsx_runtime_1.jsxs)("div", { className: "sticky", children: [(0, jsx_runtime_1.jsx)(react_bootstrap_1.Button, { onClick: () => doAction({ action: 'reset-game' }), variant: "secondary", children: "Reset" }), " ", (0, jsx_runtime_1.jsx)(ReleaseNotes_1.ReleaseNotes, { version: (0, version_1.VERSION)().join(".") }), " ", (0, jsx_runtime_1.jsxs)("span", { className: "play-timer", children: ["Play Time: ", (0, numberFormatter_1.formatSeconds)(state.timeSpentPlaying)] }), (0, jsx_runtime_1.jsxs)("span", { className: "fps", children: [(0, numberFormatter_1.formatNumber)(fps), " UPS"] })] }), (0, jsx_runtime_1.jsx)(react_bootstrap_1.Tabs, { activeKey: currentTab, onSelect: setCurrentTab, className: "section-tabs sticky", children: values_1.default.sections.map((section) => {
                    let title = ((0, jsx_runtime_1.jsxs)("span", { children: [section.Icon && ((0, jsx_runtime_1.jsx)(Sprite_1.Sprite, { name: section.Icon })), section.Name] }));
                    if (section.SubSections.every((ss) => ss.Items.every((j) => !visible[j])))
                        return null;
                    if (section.SubSections.some((ss) => ss.Items.some((j) => acknowledged[j] === false)))
                        title = ((0, jsx_runtime_1.jsxs)("span", { className: "layout-tab", children: [title, " ", (0, jsx_runtime_1.jsx)(react_bootstrap_1.Badge, { className: "new-item-tab-badge", children: "New" })] }));
                    return ((0, jsx_runtime_1.jsx)(react_bootstrap_1.Tab, { eventKey: section.Name, title: title, children: (0, jsx_runtime_1.jsx)("div", { className: "main-grid", children: sections[section.Name] }) }, section.Name));
                }) }), (0, jsx_runtime_1.jsx)(ClickAmountButtons_1.ClickAmountButtons, { current: currentClickAmount, multiClickOptions: multiClickOptions, onClick: setCurrentClickAmount }), MiniGameClass && miniGamePrize && ((0, jsx_runtime_1.jsx)(MiniGameClass, { giftRepr: (0, jsx_runtime_1.jsxs)("span", { children: [(0, jsx_runtime_1.jsx)(Sprite_1.Sprite, { name: miniGamePrize, amount: miniGameConfig?.count }), values_1.default.displayNames(miniGamePrize)] }), onSolve: () => {
                    doAction({
                        action: 'add-amount',
                        amount: miniGameConfig.count,
                        item: miniGamePrize,
                    });
                    setIsPlayingMinigame(false);
                    setMiniGamePrize(null);
                }, difficulty: miniGameConfig.difficulty, onCancel: stopMinigame }))] }));
}
exports.App = App;
